import { API_ENDPOINTS } from '../config/api'
import { USE_MOCK_API } from '../mocks/useMockApi'
import mockScans from '../mocks/fixtures/scans.json'

export interface Asset {
  id: string
  name: string
  domain?: string
  ip?: string
  type: string
  environment: string
}

export interface ScanStatus {
  scanId: string
  userId: string
  taskId: string
  targetId: string
  status: 'queued' | 'running' | 'completed' | 'failed'
  startTime: string
  lastUpdate?: string
  progress: number
  assets: Asset[]
}

export interface Vulnerability {
  name: string
  cve: string
  severity: string
  host: string
  description: string
  riskLevel: 'Critical' | 'High' | 'Medium' | 'Low'
  cvss: string
  status: string
  recommendations: string
}

export interface ScanReport {
  id: string
  name: string
  timestamp: string
  vulnerabilities: Vulnerability[]
  assetVulnerabilities: Record<string, Vulnerability[]>
  summary: {
    totalVulnerabilities: number
    critical: number
    high: number
    medium: number
    low: number
  }
}

export interface ScanResult {
  success: boolean
  scanId?: string
  message?: string
  error?: string
}

export interface ScanStatusResult {
  success: boolean
  scan?: ScanStatus
  error?: string
}

export interface ScanReportResult {
  success: boolean
  reports?: ScanReport[]
  message?: string
  error?: string
}

class ScanService {
  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('token')
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  }

  async startScan(assets: Asset[]): Promise<ScanResult> {
    try {
      const response = await fetch(API_ENDPOINTS.SCAN_START, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ assets })
      })

      const contentType = response.headers.get('content-type') || ''
      const data = contentType.includes('application/json') ? await response.json() : {}

      if (!response.ok) {
        throw new Error(data.error || 'Failed to start scan')
      }

      return data
    } catch (error) {
      console.error('Start scan error:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  async getScanStatus(scanId: string): Promise<ScanStatusResult> {
    try {
      const response = await fetch(API_ENDPOINTS.SCAN_STATUS(scanId), {
        method: 'GET',
        headers: this.getAuthHeaders()
      })

      const contentType = response.headers.get('content-type') || ''
      const data = contentType.includes('application/json') ? await response.json() : {}

      if (!response.ok) {
        throw new Error(data.error || 'Failed to get scan status')
      }

      return data
    } catch (error) {
      console.error('Get scan status error:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  async getScanReport(scanId: string): Promise<ScanReportResult> {
    try {
      const response = await fetch(API_ENDPOINTS.SCAN_REPORT(scanId), {
        method: 'GET',
        headers: this.getAuthHeaders()
      })

      const contentType = response.headers.get('content-type') || ''
      const data = contentType.includes('application/json') ? await response.json() : {}

      if (!response.ok) {
        throw new Error(data.error || 'Failed to get scan report')
      }

      return data
    } catch (error) {
      console.error('Get scan report error:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  async updateUserAssets(scanId: string): Promise<ScanResult> {
    try {
      const response = await fetch(API_ENDPOINTS.SCAN_UPDATE_ASSETS(scanId), {
        method: 'POST',
        headers: this.getAuthHeaders()
      })

      const contentType = response.headers.get('content-type') || ''
      const data = contentType.includes('application/json') ? await response.json() : {}

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update user assets')
      }

      return data
    } catch (error) {
      console.error('Update user assets error:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  async testConnection(): Promise<ScanResult> {
    try {
      const response = await fetch(API_ENDPOINTS.SCAN_TEST_CONNECTION, {
        method: 'GET',
        headers: this.getAuthHeaders()
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to test connection')
      }

      return data
    } catch (error) {
      console.error('Test connection error:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  // ========== v3.1 scan-provider ==========
  async providerStartScan(params: { orgId: string|number, targets: string[], scannerType?: 'openvas'|'cve', ports?: string }): Promise<{ success: boolean; scanId?: string; taskId?: string; error?: string }> {
    try {
      const res = await fetch(API_ENDPOINTS.SCAN_PROVIDER_START, {
        method: 'POST',
        headers: { ...this.getAuthHeaders() },
        body: JSON.stringify({
          orgId: params.orgId,
          targets: params.targets,
          scannerType: params.scannerType || 'openvas',
          ports: params.ports
        })
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data?.error || 'Failed to start scan')
      return { success: true, scanId: data.scanId, taskId: data.taskId }
    } catch (e:any) {
      return { success: false, error: e?.message || 'Failed to start scan' }
    }
  }

  async providerGetStatus(scanId: string, opts?: { force?: boolean }): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const url = API_ENDPOINTS.SCAN_PROVIDER_STATUS(scanId) + ((opts && opts.force) ? '?force=1' : '')
      const res = await fetch(url, { headers: { ...this.getAuthHeaders() } })
      const data = await res.json()
      if (!res.ok) throw new Error(data?.error || 'Failed to get status')
      return { success: true, data: data.data }
    } catch (e:any) {
      return { success: false, error: e?.message || 'Failed to get status' }
    }
  }

  async providerListScans(orgId?: string|number): Promise<any[]> {
    if (USE_MOCK_API) {
      return Promise.resolve(Array.isArray(mockScans.scans) ? mockScans.scans : [])
    }
    try {
      const res = await fetch(API_ENDPOINTS.SCAN_PROVIDER_LIST(orgId as any), { headers: { ...this.getAuthHeaders() } })
      const data = await res.json()
      if (!res.ok) throw new Error(data?.error || 'Failed to list scans')
      return Array.isArray(data.scans) ? data.scans : []
    } catch {
      return []
    }
  }

  async providerDeleteScans(
    scanIds: string[],
    orgId?: string | number,
  ): Promise<{ success: boolean; deleted?: number; failed?: number; error?: string }> {
    if (!scanIds.length) return { success: false, error: 'No scans selected' }
    try {
      const res = await fetch(API_ENDPOINTS.SCAN_PROVIDER_BULK_DELETE, {
        method: 'POST',
        headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          scanIds,
          ...(orgId != null ? { orgId: String(orgId) } : {}),
        }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data?.error || 'Failed to delete scans')
      return {
        success: true,
        deleted: data.data?.deleted ?? scanIds.length,
        failed: data.data?.failed ?? 0,
      }
    } catch (e: unknown) {
      return {
        success: false,
        error: e instanceof Error ? e.message : 'Failed to delete scans',
      }
    }
  }

  async getUserAssets(): Promise<{ success: boolean; assets?: Asset[]; error?: string }> {
    try {
      const response = await fetch(API_ENDPOINTS.SCAN_USER_ASSETS, {
        method: 'GET',
        headers: this.getAuthHeaders()
      })
      const contentType = response.headers.get('content-type') || ''
      const data = contentType.includes('application/json') ? await response.json() : {}
      if (!response.ok) {
        throw new Error(data.error || 'Failed to get user assets')
      }
      // Normalize output
      if (Array.isArray(data.assets)) {
        return { success: true, assets: data.assets as Asset[] }
      }
      return { success: true, assets: [] }
    } catch (error) {
      console.warn('Get user assets error:', error)
      // Safe fallback: return empty list instead of throwing
      return { success: true, assets: [] }
    }
  }

  async getScanHistory(): Promise<ScanStatus[]> {
    try {
      const response = await fetch(API_ENDPOINTS.SCAN_HISTORY, {
        method: 'GET',
        headers: this.getAuthHeaders()
      })
      const contentType = response.headers.get('content-type') || ''
      if (!response.ok) {
        let msg = 'Failed to get scan history'
        if (contentType.includes('application/json')) {
          const dto = await response.json().catch(() => ({} as any))
          msg = dto.error || msg
        }
        throw new Error(msg)
      }
      if (contentType.includes('application/json')) {
        const dto = await response.json().catch(() => ({} as any))
        return dto.history || []
      }
      return []
    } catch (error) {
      console.error('Get scan history error:', error)
      return []
    }
  }

  // Utility method to check if user has assets for scanning
  async hasAssetsForScanning(): Promise<boolean> {
    try {
      const result = await this.getUserAssets()
      return result.success && (result.assets?.length || 0) > 0
    } catch (error) {
      console.error('Check assets error:', error)
      return false
    }
  }

  // Utility method to get scan status with polling
  async pollScanStatus(scanId: string, onUpdate: (status: ScanStatus) => void, maxAttempts = 60): Promise<ScanStatus> {
    let attempts = 0
    
    const poll = async (): Promise<ScanStatus> => {
      attempts++
      
      const result = await this.getScanStatus(scanId)
      
      if (!result.success || !result.scan) {
        throw new Error(result.error || 'Failed to get scan status')
      }

      onUpdate(result.scan)

      // Stop polling if scan is completed or failed
      if (result.scan.status === 'completed' || result.scan.status === 'failed') {
        return result.scan
      }

      // Stop polling if max attempts reached
      if (attempts >= maxAttempts) {
        throw new Error('Scan status polling timeout')
      }

      // Wait 5 seconds before next poll
      await new Promise(resolve => setTimeout(resolve, 5000))
      
      return poll()
    }

    return poll()
  }
}

export default new ScanService()